import { useState, useEffect } from "react";
import { Switch } from "@mui/material";
import {
  Variable,
  VariableType,
  VariableScope,
  createVariable,
} from "../../../models/interactions/Variable";
import { isBuiltInVariableName } from "../../../models/interactions/BuiltInSequenceVariables";
import PiInputfield from "../../../components/PiInputField";
import PiButton from "../../../components/PiButton";
import VariableTypeSelector from "./VariableTypeSelector";

interface NewVariableFormProps {
  onSave: (variable: Variable) => void;
  onCancel: () => void;
  existingVariables: Variable[];
}

export default function NewVariableForm({
  onSave,
  onCancel,
  existingVariables,
}: NewVariableFormProps) {
  const [name, setName] = useState("");
  const [type, setType] = useState<VariableType>(VariableType.String);
  const [stringValue, setStringValue] = useState("");
  const [numberValue, setNumberValue] = useState("");
  const [booleanValue, setBooleanValue] = useState(false);

  // Reset values when type changes
  useEffect(() => {
    if (type === VariableType.Boolean) {
      setBooleanValue(false);
    } else if (type === VariableType.Number) {
      setNumberValue("");
    } else {
      setStringValue("");
    }
  }, [type]);

  const handleCreate = () => {
    // Validate unique name
    const nameExists = existingVariables.some((v) => v.name === name.trim());
    if (nameExists) {
      alert("Variable name must be unique");
      return;
    }

    if (!name.trim()) {
      alert("Variable name is required");
      return;
    }
    if (isBuiltInVariableName(name.trim())) {
      alert("This variable name is reserved for a built-in variable");
      return;
    }

    let value: boolean | string | number;
    if (type === VariableType.Boolean) {
      value = booleanValue;
    } else if (type === VariableType.Number) {
      const num = Number(numberValue);
      if (isNaN(num) || numberValue.trim() === "") {
        alert("Please enter a valid number");
        return;
      }
      value = num;
    } else {
      if (stringValue.trim() === "") {
        alert("Please enter a value");
        return;
      }
      value = stringValue;
    }

    const newVariable = createVariable(
      name.trim(),
      type,
      value,
      VariableScope.Public
    );
    onSave(newVariable);
  };

  const isFormValid = () => {
    if (name.trim().length === 0) return false;
    if (isBuiltInVariableName(name.trim())) return false;
    if (type === VariableType.Boolean) {
      return true; // Boolean always has a value
    } else if (type === VariableType.Number) {
      const num = Number(numberValue);
      return !isNaN(num) && numberValue.trim() !== "";
    } else {
      return stringValue.trim().length > 0;
    }
  };

  const renderInitialValueInput = () => {
    switch (type) {
      case VariableType.Boolean:
        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={booleanValue}
              onChange={(event, checked) => setBooleanValue(checked)}
            />
            <span className="text-[12px] text-creator-text-sub font-poppins">
              {booleanValue ? "True" : "False"}
            </span>
          </div>
        );
      case VariableType.Number:
        return (
          <PiInputfield
            label=""
            placeholder="0"
            value={numberValue}
            onChange={setNumberValue}
            square
          />
        );
      case VariableType.String:
        return (
          <PiInputfield
            label=""
            placeholder="Enter text"
            value={stringValue}
            onChange={setStringValue}
            square
          />
        );
    }
  };

  return (
    <div className="flex flex-col gap-4 p-2 w-full h-full font-poppins">
      {/* Variable Name */}
      <div className="flex flex-col gap-2">
        <label className="text-[12px] font-medium text-creator-text-sub tracking-wide font-poppins">
          Variable Name
        </label>
        <PiInputfield
          label=""
          placeholder="e.g. scoreCounter"
          value={name}
          onChange={setName}
          autoFocus
          square
        />
      </div>

      {/* Variable Type */}
      <div className="flex flex-col gap-2">
        <label className="text-[12px] font-medium text-creator-text-sub tracking-wide font-poppins">
          Variable Type
        </label>
        <VariableTypeSelector selectedType={type} onSelect={setType} />
      </div>

      {/* Initial Value */}
      <div className="flex flex-col gap-2">
        <label className="text-[12px] font-medium text-creator-text-sub tracking-wide font-poppins">
          Initial Value
        </label>
        {renderInitialValueInput()}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end mt-auto">
        <div className="w-[120px] h-[36px]">
          <PiButton variant="piBlueOutlined" onClick={onCancel}>
            Cancel
          </PiButton>
        </div>
        <div className="w-[120px] h-[36px]">
          <PiButton
            variant="piBlue"
            onClick={handleCreate}
            disabled={!isFormValid()}
          >
            Confirm
          </PiButton>
        </div>
      </div>
    </div>
  );
}
